import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Upload, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import API from '../config/api';

export default function S3ImageUpload({ value, onChange, onUploadingChange, disabled = false }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const uploadImage = async event => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = '';
    if (!file.type.startsWith('image/') || file.size > 15 * 1024 * 1024) {
      toast.error('Choose an image smaller than 15MB');
      return;
    }
    const data = new FormData();
    data.append('image', file);
    setUploading(true);
    onUploadingChange?.(true);
    try {
      const response = await axios.post(`${API}/upload/bhet-image`, data, { withCredentials: true });
      if (response.data.storage !== 's3' || !response.data.url) throw new Error('Image was not uploaded to S3');
      onChange(response.data.url);
      toast.success('Image uploaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Image upload failed. Please try again.');
    } finally {
      setUploading(false);
      onUploadingChange?.(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-3">
      {value && (
        <div className="relative h-24 w-24 shrink-0">
          <img src={value.startsWith('/') ? `${API.replace(/\/api$/, '')}${value}` : value}
            alt="Offering preview" className="h-full w-full rounded-xl border border-gray-200 object-cover" />
          <button type="button" aria-label="Remove image" disabled={disabled || uploading}
            onClick={() => onChange('')} className="absolute -right-2 -top-2 rounded-full bg-white p-1.5 text-red-600 shadow disabled:opacity-50">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      <div className="space-y-2">
        <button type="button" disabled={disabled || uploading} onClick={() => inputRef.current?.click()}
          className="inline-flex items-center gap-2 rounded-xl border border-gray-200 px-4 py-2.5 text-sm hover:bg-orange-50 disabled:opacity-50">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          {uploading ? 'Uploading...' : value ? 'Replace image' : 'Upload image'}
        </button>
        <p className="text-xs text-gray-500">Choose an image up to 15MB.</p>
      </div>
      <input ref={inputRef} type="file" accept="image/*" aria-label="Offering image"
        className="hidden" disabled={disabled || uploading} onChange={uploadImage} />
    </div>
  );
}
